import { AgenticEnvelope } from "../../documentation/models/services/AgenticMetadataDTO";
import { AgenticSpaceDTO, ApproveRequestDTO, ChangesetDTO, ChangesetRebaseDTO, ChangesetRebaseRequestDTO, ChangesetRevisionDTO, PageContentDTO, PushPolicy, SpacePageDTO, UserNameDTO, WhoAmIDTO } from "../models/ChangeRequestDTO";
import { ConfigurationProvider } from "../../../lib/infrastructure/configuration/ConfigurationProvider";
import { HttpClient } from "../../../lib/infrastructure/http/HttpClient";
import { IdentityService } from "../../../lib/infrastructure/identity/IdentityService";
import { UrlUtility } from "../../../lib/utils/UrlUtility";

const Route = (path: string) =>
    UrlUtility.join(ConfigurationProvider.getConfiguration().App.BackendUrl, path);

const ClientHeaders = { "X-Orbital-Client": "ark-web" };

const spacePath = (spaceId: string | number) => `/api/agentic/v1/spaces/${encodeURIComponent(String(spaceId))}`;

/** What the change-request screens need; the components take it as a prop so tests can pass a fake. */
export interface IChangeRequestService {
    list(spaceId: string | number, state?: string, take?: number): Promise<ChangesetDTO[]>;
    get(id: string): Promise<ChangesetDTO>;
    /** Applies the proposal; `request.revision` names the one read (refused when it moved on), `request.update` rebases first. */
    approve(id: string, request?: ApproveRequestDTO): Promise<ChangesetDTO>;
    reject(id: string, reason: string): Promise<ChangesetDTO>;
    withdraw(id: string): Promise<ChangesetDTO>;
    /**
     * The author merges the edits made to the pages since the proposal (three-way, all or nothing), settling reported conflicts with
     * `request.resolutions`; `request.dryRun` only reports.
     */
    rebase(id: string, request?: ChangesetRebaseRequestDTO): Promise<ChangesetRebaseDTO>;
    /** What an update would do (merge, conflicts, issues), for anyone who sees the change request; nothing is saved. */
    previewRebase(id: string): Promise<ChangesetRebaseDTO>;
    /** The revisions of a change request, oldest first, without ops. */
    getRevisions(id: string): Promise<ChangesetRevisionDTO[]>;
    /** One revision with its ops. */
    getRevision(id: string, revision: number): Promise<ChangesetRevisionDTO>;
    /** Display names of user ids (authors, reviewers, page editors); ids the directory does not know are left out. */
    getUserNames(ids: string[]): Promise<UserNameDTO[]>;
    getSpace(spaceId: string | number): Promise<AgenticSpaceDTO>;
    setPushPolicy(spaceId: string | number, pushPolicy: PushPolicy): Promise<AgenticSpaceDTO>;
    /** The space's pages from its manifest: titles and tree to describe ops, etags to spot stale ones. */
    getPages(spaceId: string | number): Promise<SpacePageDTO[]>;
    /** Current bodies of existing pages (`pages/content`, at most 500 ids), to diff the proposed ones against. */
    getPageContents(spaceId: string | number, ids: number[]): Promise<PageContentDTO[]>;
    /** The id the server stamps as `authorId` (the `oid` claim); falls back to `GET whoami`. */
    getCurrentUserId(): Promise<string | undefined>;
}

/** The facade's error message when it sent one (envelope `error.message`), else the transport's. */
export function errorMessage(err: any): string {
    return err?.response?.data?.error?.message || err?.message || "Unexpected error";
}

function unwrap<T>(envelope: AgenticEnvelope<T> | undefined): T {
    if (!envelope) throw new Error("Empty response from the server.");
    if (envelope.error) throw new Error(envelope.error.message || envelope.error.code);
    return envelope.data as T;
}

/**
 * Change requests (proposed changesets) of a space through Ark.Core's agentic facade (`api/agentic/v1`),
 * the same routes the orbital CLI uses (`orbital ark proposals …`).
 */
export class ChangeRequestService implements IChangeRequestService {
    async list(spaceId: string | number, state?: string, take: number = 50): Promise<ChangesetDTO[]> {
        const params: Record<string, string | number> = { take };
        if (state && state !== "all") params.state = state;
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetDTO[]>>({
            method: "GET",
            url: Route(`${spacePath(spaceId)}/changesets`),
            params,
            headers: ClientHeaders,
        });
        return unwrap(res.data) || [];
    }

    async get(id: string): Promise<ChangesetDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetDTO>>({
            method: "GET",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}`),
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async approve(id: string, request?: ApproveRequestDTO): Promise<ChangesetDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetDTO>>({
            method: "POST",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/approve`),
            data: request,
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async getRevisions(id: string): Promise<ChangesetRevisionDTO[]> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetRevisionDTO[]>>({
            method: "GET",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/revisions`),
            headers: ClientHeaders,
        });
        return unwrap(res.data) || [];
    }

    async getRevision(id: string, revision: number): Promise<ChangesetRevisionDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetRevisionDTO>>({
            method: "GET",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/revisions/${revision}`),
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async getUserNames(ids: string[]): Promise<UserNameDTO[]> {
        const wanted = Array.from(new Set(ids.filter(Boolean)));
        if (wanted.length === 0) return [];
        const res = await HttpClient.sessionRequest<AgenticEnvelope<UserNameDTO[]>>({
            method: "GET",
            url: Route(`/api/agentic/v1/users`),
            params: { ids: wanted.join(",") },
            headers: ClientHeaders,
        });
        return unwrap(res.data) || [];
    }

    async reject(id: string, reason: string): Promise<ChangesetDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetDTO>>({
            method: "POST",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/reject`),
            data: { reason },
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async withdraw(id: string): Promise<ChangesetDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetDTO>>({
            method: "POST",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/withdraw`),
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async rebase(id: string, request?: ChangesetRebaseRequestDTO): Promise<ChangesetRebaseDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetRebaseDTO>>({
            method: "POST",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/rebase`),
            data: request,
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async previewRebase(id: string): Promise<ChangesetRebaseDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<ChangesetRebaseDTO>>({
            method: "GET",
            url: Route(`/api/agentic/v1/changesets/${encodeURIComponent(id)}/rebase`),
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async getSpace(spaceId: string | number): Promise<AgenticSpaceDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<AgenticSpaceDTO>>({
            method: "GET",
            url: Route(spacePath(spaceId)),
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async setPushPolicy(spaceId: string | number, pushPolicy: PushPolicy): Promise<AgenticSpaceDTO> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<AgenticSpaceDTO>>({
            method: "PATCH",
            url: Route(spacePath(spaceId)),
            data: { pushPolicy },
            headers: ClientHeaders,
        });
        return unwrap(res.data);
    }

    async getPages(spaceId: string | number): Promise<SpacePageDTO[]> {
        const res = await HttpClient.sessionRequest<AgenticEnvelope<{ pages?: SpacePageDTO[] }>>({
            method: "GET",
            url: Route(`${spacePath(spaceId)}/manifest`),
            headers: ClientHeaders,
        });
        return unwrap(res.data)?.pages || [];
    }

    async getPageContents(spaceId: string | number, ids: number[]): Promise<PageContentDTO[]> {
        if (ids.length === 0) return [];
        const res = await HttpClient.sessionRequest<AgenticEnvelope<PageContentDTO[]>>({
            method: "POST",
            url: Route(`${spacePath(spaceId)}/pages/content`),
            data: { ids: ids.slice(0, 500) },
            headers: ClientHeaders,
        });
        return unwrap(res.data) || [];
    }

    async getCurrentUserId(): Promise<string | undefined> {
        try {
            const oid = IdentityService.Global?.getClaims()?.oid;
            if (oid) return oid;
        } catch {
            /* no stored claims - ask the server */
        }
        const res = await HttpClient.sessionRequest<AgenticEnvelope<WhoAmIDTO>>({
            method: "GET",
            url: Route(`/api/agentic/v1/whoami`),
            headers: ClientHeaders,
        });
        const id = unwrap(res.data)?.id;
        return id && id !== "unknown" ? id : undefined;
    }
}

let defaultInstance: ChangeRequestService | undefined;

/** The shared instance the screens use when no service prop is passed. */
export function changeRequestService(): IChangeRequestService {
    if (!defaultInstance) defaultInstance = new ChangeRequestService();
    return defaultInstance;
}

export const ChangeRequestsChangedEvent = "change-requests-changed";

/** Fired after an approve/reject/withdraw so the proposed-count badge refreshes. */
export function dispatchChangeRequestsChanged() {
    window.dispatchEvent(new CustomEvent<void>(ChangeRequestsChangedEvent));
}
